import { forwardRef, useId, type ComponentPropsWithRef } from "react";
import { type PropsWithOptionalTestID, CoreToggle } from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";

import * as S from "./Toggle.styled";
import { InputLabelTop, type InputLabelTopProps } from "../InputLabelTop";
import { ToggleWrapper } from "./ToggleWrapper/index";

export type ToggleProps = PropsWithOptionalTestID &
  Omit<ComponentPropsWithRef<"input">, "type" | "style" | "className"> & {
    labelProps: Omit<
      InputLabelTopProps,
      "htmlFor" | "labelHintID" | "children"
    >;
    toggleSize?: CoreToggle.Size;
  };

const Toggle = forwardRef<HTMLDivElement, ToggleProps>((props, ref) => {
  const {
    id,
    labelProps,
    testID = "toggle",
    toggleSize = "default",
    ...inputProps
  } = props;

  const fallbackID = useId();
  const labelHintID = useId();

  const { labelHint } = labelProps;

  const {
    tokens: { toggle },
  } = useDesignTokens();

  return (
    <div ref={ref}>
      <InputLabelTop
        {...labelProps}
        labelHintID={labelHintID}
        htmlFor={id || fallbackID}
      >
        <ToggleWrapper toggleSize={toggleSize}>
          <S.ToggleOuter $toggleSize={toggleSize}>
            <S.HiddenInput
              {...inputProps}
              id={id || fallbackID}
              role="switch"
              type="checkbox"
              aria-describedby={labelHint ? labelHintID : undefined}
              data-testid={testID}
            />

            <S.TextValuesContainer>
              <S.CheckedTextValue
                aria-hidden={true}
                $toggleSize={toggleSize}
                component="span"
                variant={toggle.text.size[toggleSize].typography.variant}
                fontFamily={toggle.text.size[toggleSize].typography.fontFamily}
              >
                On
              </S.CheckedTextValue>

              <S.UncheckedTextValue
                aria-hidden={true}
                $toggleSize={toggleSize}
                component="span"
                variant={toggle.text.size[toggleSize].typography.variant}
                fontFamily={toggle.text.size[toggleSize].typography.fontFamily}
              >
                Off
              </S.UncheckedTextValue>
            </S.TextValuesContainer>

            <S.ToggleInner $toggleSize={toggleSize} />
          </S.ToggleOuter>
        </ToggleWrapper>
      </InputLabelTop>
    </div>
  );
});

Toggle.displayName = "Toggle";

export default Toggle;
